import { useState } from "react";
import { useTranslation } from "react-i18next";
import { readAuthoredItem } from "../../../../api/content/authoring/processAuthoringApi";
import { annotationSetContent } from "../../../../api/content/documentAnnotationsApi";
import type { AnnotationSet } from "../../../../api/threads/liveMessages";
import AppButton from "../../../../ui/buttons/AppButton";
import EditableAnnotationSet from "../../../annotation/EditableAnnotationSet";
import ChangeSetPicker from "../../../changeSets/ChangeSetPicker";
import type { ChangeSetChoice } from "../../../changeSets/changeSetChoice";
import { EditSessionBar, useEditSession } from "../../../editing";
import { itemEditAt, replacingTargetOf } from "../../../processAuthoring/draft/itemEdits";
import type { StepSave } from "../../../processAuthoring/draft/changeSetStep";
import StepSaveNotice from "../../../processAuthoring/draft/StepSaveNotice";
import { useStepBeforeThread } from "../../../threads/prepare/author/editStep";

/** The one staged edit: the whole set as the person changed it. */
const EDITED_SET = "annotations";

interface AnnotationSetEditProps {
  readonly projectId: string;
  /** The merged artifact version, `<item id>@<blob id>`. */
  readonly reference: string;
  readonly name: string;
  readonly set: AnnotationSet;
  /** Leave the editor; with unsaved edits the draft guard asks first. */
  readonly onClose: () => void;
}

/**
 * A merged content annotation set edited in place on Documents (W-15; F88: "the v1 span view
 * with edit, add and delete labels"; F96: one Save or Discard). The edit runs as the "Edit an
 * item" step (ADR-0031 D2, CP-06): Save starts its Thread and answers it with a change set
 * that replaces the artifact's content, which curators then review. Opening it writes nothing.
 */
export default function AnnotationSetEdit({ projectId, reference, name, set, onClose }: AnnotationSetEditProps) {
  const { t } = useTranslation();
  const step = useStepBeforeThread({ projectId, title: t("threads.prepare.author.editTitle", { name }), edited: { kind: "item", reference } });
  const defaultTitle = t("threads.prepare.author.editTitle", { name });
  const [choice, setChoice] = useState<ChangeSetChoice>({ kind: "new", title: defaultTitle });
  const [saved, setSaved] = useState<StepSave | null>(null);
  // What this editor last saved: the next edits start from it, so a second save keeps the first (Sol S2).
  const [baseline, setBaseline] = useState(set);
  const session = useEditSession<AnnotationSet>(async (edits) => {
    const edited = edits.at(-1);
    if (edited === undefined) return;
    const item = await readAuthoredItem(reference);
    if (item.content?.kind !== "artifact") throw new Error("The annotation set is no longer an artifact.");
    const artifact = { ...item.content.definition, content: annotationSetContent(edited) };
    const target = replacingTargetOf({ itemId: item.itemId, reference });
    setSaved(await step.save(projectId, choice, [async (head) => [await itemEditAt(projectId, head, target, artifact)]]));
    setBaseline(edited);
  });
  const shown = session.stagedOf(EDITED_SET)?.edit ?? baseline;
  return (
    <div className="space-y-3">
      <AppButton size="xs" variant="ghost" onClick={() => { session.requestClose(onClose); }}>{t("sources.document.added.stopEditing")}</AppButton>
      <EditableAnnotationSet set={shown} onChange={(next) => { session.stage(EDITED_SET, next); }} />
      <EditSessionBar session={session} saveLabel={t("sources.document.added.proposeEdits")} saveDisabled={choice.kind === "new" && choice.title.trim() === ""}>
        <ChangeSetPicker projectId={projectId} choice={choice} onChange={setChoice} defaultTitle={defaultTitle} />
      </EditSessionBar>
      {saved !== null ? <StepSaveNotice saved={saved} shownIn={step.shownIn} /> : null}
    </div>
  );
}
